import { workspace } from '../../../shared/api';
import {
  AddQuotaAlert,
  AddQuotaAlertType,
  SNCR_QUOTA_TYPES,
  SNCR_REGULAR_QUOTA_TYPES,
  SNCR_SPECIAL_QUOTA_TYPES,
  SncrAddDoctorQuotaResponse,
  SncrAddQuotaPayload,
  SncrDoctorQuotaGroupedByType,
  SncrNumberingRequestItem,
  SncrNumberingRequestResult,
  SncrPrescriptionTypeId,
  SncrRegularQuotaType,
  SncrSpecialQuotaType,
  SncrStock,
} from './sncrTypes';

const doctorPath = (doctorHash: string) => `/sncr/doctor/${doctorHash}`;

function isQuotaType(value: unknown): value is SncrPrescriptionTypeId {
  return typeof value === 'string' && (SNCR_QUOTA_TYPES as readonly string[]).includes(value);
}

function isRegularQuotaType(type: SncrPrescriptionTypeId): type is SncrRegularQuotaType {
  return (SNCR_REGULAR_QUOTA_TYPES as readonly string[]).includes(type);
}

function isSpecialQuotaType(type: SncrPrescriptionTypeId): type is SncrSpecialQuotaType {
  return (SNCR_SPECIAL_QUOTA_TYPES as readonly string[]).includes(type);
}

function normalizeDoctorQuota(data: unknown): SncrStock[] {
  if (!Array.isArray(data)) throw new Error('Resposta inválida ao consultar cotas SNCR.');

  const quantitiesByType = new Map<SncrPrescriptionTypeId, number>();

  data.forEach((rawItem: unknown) => {
    const item = rawItem as Partial<SncrDoctorQuotaGroupedByType>;
    if (!isQuotaType(item?.type)) return;
    if (typeof item.quantity !== 'number' || !Number.isFinite(item.quantity) || item.quantity < 0) {
      throw new Error(`Quantidade inválida para a cota ${item.type}.`);
    }
    quantitiesByType.set(item.type, item.quantity);
  });

  return SNCR_QUOTA_TYPES.map((type) => ({
    type,
    balance: quantitiesByType.get(type) ?? 0,
    enabled: quantitiesByType.has(type),
  }));
}

function buildAddQuotaPayload(items: SncrNumberingRequestItem[]): SncrAddQuotaPayload {
  const regularQuotas = items
    .filter((item): item is SncrNumberingRequestItem & { type: SncrRegularQuotaType } => isRegularQuotaType(item.type))
    .map(({ type, quantity }) => ({ type, quantity }));

  const specialQuotas = items
    .filter((item): item is SncrNumberingRequestItem & { type: SncrSpecialQuotaType } => isSpecialQuotaType(item.type))
    .map(({ type }) => ({ type }));

  if (regularQuotas.length === 0 && specialQuotas.length === 0) {
    throw new Error('Nenhuma cota SNCR foi selecionada.');
  }

  return {
    ...(regularQuotas.length > 0 ? { regularQuotas } : {}),
    ...(specialQuotas.length > 0 ? { specialQuotas } : {}),
  };
}

function normalizeAlerts(rawAlerts: unknown): AddQuotaAlert[] | undefined {
  if (!Array.isArray(rawAlerts)) return undefined;

  const alerts = rawAlerts.reduce<AddQuotaAlert[]>((acc, rawAlert: unknown) => {
    const alert = rawAlert as Partial<AddQuotaAlert>;
    if (typeof alert?.code !== 'string' || (alert.type !== 'WARNING' && alert.type !== 'ERROR')) {
      return acc;
    }
    acc.push({
      code: alert.code,
      type: alert.type as AddQuotaAlertType,
      ...(alert.data && typeof alert.data === 'object' ? { data: alert.data as Record<string, unknown> } : {}),
    });
    return acc;
  }, []);

  return alerts.length > 0 ? alerts : undefined;
}

function normalizeAddQuotaResponse(data: unknown): SncrAddDoctorQuotaResponse[] {
  if (!Array.isArray(data)) throw new Error('Resposta inválida ao solicitar cotas SNCR.');

  return data.map((rawItem: unknown) => {
    const item = rawItem as Partial<SncrAddDoctorQuotaResponse>;
    if (!isQuotaType(item?.type) || typeof item.success !== 'boolean') {
      throw new Error('Item inválido na resposta de solicitação de cotas SNCR.');
    }

    const alerts = normalizeAlerts(item.alerts);

    return {
      type: item.type,
      success: item.success,
      ...(typeof item.errorMessage === 'string' && item.errorMessage.trim() ? { errorMessage: item.errorMessage } : {}),
      ...(alerts ? { alerts } : {}),
    };
  });
}

export const SncrService = {
  async getDoctorQuota(doctorHash: string): Promise<SncrStock[]> {
    const { data } = await workspace.get(`${doctorPath(doctorHash)}/quota`);
    return normalizeDoctorQuota(data);
  },

  async isDoctorAuthenticated(doctorHash: string): Promise<boolean> {
    const { data } = await workspace.get(`${doctorPath(doctorHash)}/is-authed`);
    return data === true;
  },

  async authenticateDoctor(doctorHash: string, sessionId: string): Promise<void> {
    await workspace.post(`${doctorPath(doctorHash)}/auth`, { sessionId });
  },

  async addDoctorQuota(doctorHash: string, items: SncrNumberingRequestItem[]): Promise<SncrNumberingRequestResult> {
    const payload = buildAddQuotaPayload(items);
    const { data } = await workspace.post(`${doctorPath(doctorHash)}/add-quota`, payload);
    const responseByType = new Map(normalizeAddQuotaResponse(data).map((item) => [item.type, item]));

    return {
      items: items.map((item) => {
        const response = responseByType.get(item.type);
        if (!response?.success) {
          return {
            ...item,
            status: 'failed' as const,
            ...(response?.errorMessage ? { errorMessage: response.errorMessage } : {}),
            ...(response?.alerts ? { alerts: response.alerts } : {}),
          };
        }
        return { ...item, status: 'fulfilled' as const };
      }),
    };
  },
};
